import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { AlertTriangle, FolderKanban, Heart, Layers, LayoutGrid, ListChecks, Loader2, Trash2, X } from "lucide-react";
import { ResultGrid } from "@/components/ResultGrid";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { useProjectStore } from "@/store/project";
import { useAuthStore } from "@/store/auth";
import { Select } from "@/components/ui/select";
import {
  moveJobs,
  trashJobs,
  permanentlyDeleteJob,
  restoreJobFromTrash,
  swrFetcher,
  type JobInfo,
  type ProjectInfo,
  type TeamMember,
} from "@/lib/api";
import { EmptyState } from "@/components/ui/empty-state";
import { GALLERY_PAGE_SIZE, galleryCursor, galleryPageKey, type GalleryFilter, type GalleryMedia } from "@/lib/galleryQuery";
import { MediaFilterTabs } from "@/components/MediaFilterTabs";
import { SelectionToolbar } from "@/components/SelectionToolbar";
import { MoveToProjectDialog } from "@/components/MoveToProjectDialog";
import { MoveResultBar } from "@/components/MoveResultBar";
import { useProjectMove } from "@/hooks/useProjectMove";
import { sharedProjectId } from "@/lib/projectMove";
import { useBulkDownload } from "@/hooks/useBulkDownload";
import { useJobSelection } from "@/hooks/useJobSelection";
import { useGalleryColumns } from "@/hooks/useGalleryColumns";
import { GallerySizeSlider } from "@/components/GallerySizeSlider";
import { downloadPlan } from "@/lib/selection";

/** One card action carries a single job; the selection toolbar carries every manageable selected job. */
type DestructiveAction = { type: "trash" | "purge"; jobs: JobInfo[]; skipped?: number };

/** Selection toolbar and move result share one slot above the mobile bottom nav. */
const FLOATING_BAR =
  "fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.5rem)] left-1/2 z-30 w-[calc(100%-1rem)] -translate-x-1/2 justify-center sm:w-auto sm:max-w-[calc(100%-2rem)] lg:bottom-6 lg:left-[calc(50%+7.5rem)]";

const FILTER_OPTIONS = [
  { value: "all" as const, label: "All" },
  { value: "completed" as const, label: "Completed" },
  {
    value: "favorites" as const,
    label: (
      <span className="inline-flex items-center gap-1.5">
        <Heart className="h-3.5 w-3.5" />
        Favorites
      </span>
    ),
  },
  {
    value: "trash" as const,
    label: (
      <span className="inline-flex items-center gap-1.5">
        <Trash2 className="h-3.5 w-3.5" />
        Trash
      </span>
    ),
  },
];

export function GalleryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = searchParams.get("batch_id");
  const [filter, setFilter] = useState<GalleryFilter>("all");
  const [media, setMedia] = useState<GalleryMedia>("all");
  const [pendingAction, setPendingAction] = useState<DestructiveAction | null>(null);
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectModeOn, setSelectModeOn] = useState(false);
  // Select-all keeps loading pages until the whole scope is on screen.
  const [selectingAll, setSelectingAll] = useState(false);
  const grid = useGalleryColumns();
  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const activeTeam = useAuthStore((s) => s.activeTeam);
  const [ownerId, setOwnerId] = useState("");
  useEffect(() => setOwnerId(""), [activeTeam?.id]);
  const isAdmin = ["super_admin", "org_admin", "team_admin"].includes(activeTeam?.role ?? "");
  const { data: members } = useSWR<TeamMember[]>(
    isAdmin && activeTeam ? `/api/teams/${activeTeam.id}/members` : null, swrFetcher
  );

  // History jobs, scoped to the selected project (null = team-wide / "All"), paged
  // by keyset cursor. Every filter is answered by the query, never by trimming a page.
  const {
    data: pages,
    error: loadError,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate,
  } = useSWRInfinite<JobInfo[]>(
    (index, previous) =>
      previous && previous.length < GALLERY_PAGE_SIZE
        ? null
        : galleryPageKey({ filter, media, batchId, projectId: batchId ? null : selectedProjectId, userId: isAdmin ? ownerId : null, page: index, cursor: galleryCursor(previous) }),
    swrFetcher, { refreshInterval: batchId ? 3000 : 0, errorRetryCount: 2, errorRetryInterval: 3000 }
  );

  // Resolve the active project name for the header scope label.
  const { data: projects } = useSWR<ProjectInfo[]>(
    "/api/projects?include_archived=false",
    swrFetcher
  );
  const selectedProject = selectedProjectId
    ? (projects ?? []).find((p) => p.id === selectedProjectId) ?? null
    : null;
  const scopeLabel = batchId ? `Batch ${batchId.slice(0, 8)}` : selectedProjectId ? selectedProject?.name ?? "Project" : "All projects";

  // Unique by id: selection and counts must match what the server holds.
  const jobs = useMemo(() => {
    const seen = new Set<string>();
    return (pages ?? []).flat().filter((job) => (seen.has(job.id) ? false : (seen.add(job.id), true)));
  }, [pages]);
  const selection = useJobSelection(jobs);
  const bulkDownload = useBulkDownload();
  const lastPage = pages?.[pages.length - 1];
  const hasMore = !!lastPage && lastPage.length === GALLERY_PAGE_SIZE;
  const loadingMore = isValidating && !loadError && size > 0 && !!pages && pages[size - 1] === undefined;
  const isTrash = filter === "trash";
  const selectMode = !isTrash && (selectModeOn || selection.ids.size > 0 || selectingAll);
  const { selectAll } = selection;

  useEffect(() => {
    if (!selectingAll) return;
    if (loadError) {
      setSelectingAll(false);
      return;
    }
    if (isValidating) return;
    if (hasMore) {
      // Ask for several pages per pass; the key stops at the first short page.
      void setSize(size + 8);
      return;
    }
    selectAll();
    setSelectingAll(false);
  }, [selectingAll, loadError, isValidating, hasMore, size, setSize, selectAll]);

  // A new scope or filter starts without a selection mode.
  useEffect(() => {
    setSelectModeOn(false);
    setSelectingAll(false);
  }, [filter, media, ownerId, selectedProjectId, batchId]);

  function exitSelection() {
    setSelectModeOn(false);
    setSelectingAll(false);
    selection.clear();
    bulkDownload.reset();
  }
  const isPurging = pendingAction?.type === "purge";
  const pendingCount = pendingAction?.jobs.length ?? 0;
  const pendingPrompt = pendingCount === 1 ? pendingAction?.jobs[0].params.prompt : undefined;
  const projectMove = useProjectMove({
    move: moveJobs,
    onMoved: () => {
      setSelectModeOn(false);
      selection.clear();
      bulkDownload.reset();
      return mutate();
    },
  });

  function openMove(targets: JobInfo[]) {
    const manageable = targets.filter((job) => job.can_manage);
    if (!manageable.length) {
      setActionError("Only your own generations can be moved to another project.");
      return;
    }
    setActionError(null);
    projectMove.dismiss();
    projectMove.open(manageable.map((job) => job.id), {
      skipped: targets.length - manageable.length,
      currentProjectId: sharedProjectId(manageable),
    });
  }

  async function restore(job: JobInfo) {
    if (busyJobId) return;
    setActionError(null);
    setBusyJobId(job.id);
    try {
      await restoreJobFromTrash(job.id);
      await mutate();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "The generation could not be restored.");
    } finally {
      setBusyJobId(null);
    }
  }

  async function confirmAction() {
    if (!pendingAction || busyJobId) return;
    const { jobs: targets, type } = pendingAction;
    setActionError(null);
    try {
      if (type === "trash") {
        // One bulk request per 200 ids; only completed generations are
        // selectable, and the server skips anything it may not trash.
        setBusyJobId(targets[0]?.id ?? "bulk");
        await trashJobs(targets.map((job) => job.id));
      } else {
        // Permanent deletes stay one request per job, in order; a repeat is
        // idempotent server-side, so a retry after a mid-way failure is safe.
        for (const job of targets) {
          setBusyJobId(job.id);
          await permanentlyDeleteJob(job.id);
        }
      }
      setPendingAction(null);
      setSelectModeOn(false);
      selection.clear();
      await mutate();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "The action could not be completed.");
      await mutate();
    } finally {
      setBusyJobId(null);
    }
  }

  function trashSelected() {
    const manageable = selection.selected.filter((job) => job.can_manage);
    if (!manageable.length) {
      setActionError("Only your own generations can be moved to Trash.");
      return;
    }
    setPendingAction({ type: "trash", jobs: manageable, skipped: selection.selected.length - manageable.length });
  }

  function closeDialog() {
    if (busyJobId) return;
    setPendingAction(null);
    setActionError(null);
  }

  return (
    <div className="page-container max-w-7xl">
      <div className="page-header sm:items-center">
        <div className="flex items-center gap-3">
          <LayoutGrid className="h-5 w-5 text-studio-accent" />
          <h1 className="font-display text-xl font-bold tracking-tight">Gallery</h1>
          <span className="flex items-center gap-1.5 rounded-lg border border-studio-border bg-studio-card px-2.5 py-1 text-xs text-studio-muted">
            {selectedProjectId ? (
              <FolderKanban className="h-3.5 w-3.5 text-studio-accent" />
            ) : (
              <Layers className="h-3.5 w-3.5 text-studio-muted" />
            )}
            <span className="max-w-[180px] truncate">{scopeLabel}</span>
            {batchId && <button type="button" onClick={() => { const next = new URLSearchParams(searchParams); next.delete("batch_id"); setSearchParams(next); }} className="text-studio-accent">Clear batch filter</button>}
          </span>
        </div>
        <SegmentedControl options={FILTER_OPTIONS} value={filter} onChange={setFilter} className="w-full sm:w-auto" />
      </div>
      <div className="mb-4 sm:max-w-md"><MediaFilterTabs value={media} onChange={setMedia} /></div>
      {isAdmin && <div className="mb-4 max-w-xs"><label htmlFor="gallery-owner" className="mb-1 block text-xs text-studio-muted">Owner</label><Select id="gallery-owner" value={ownerId} onChange={(event) => setOwnerId(event.target.value)}><option value="">All owners</option>{(members ?? []).map((member) => <option key={member.user_id} value={member.user_id}>{member.full_name || member.email}</option>)}</Select></div>}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {isTrash ? <span /> : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-pressed={selectMode}
            onClick={() => (selectMode ? exitSelection() : setSelectModeOn(true))}
            className="h-10 sm:h-9"
          >
            {selectMode ? <X className="h-4 w-4" /> : <ListChecks className="h-4 w-4" />}
            {selectMode ? "Done" : "Select"}
          </Button>
        )}
        <GallerySizeSlider screen={grid.screen} value={grid.columns} onChange={grid.setColumns} />
      </div>

      {actionError && pendingAction === null && (
        <div role="alert" className="mb-4 flex items-start gap-2 rounded-xl border border-studio-danger/25 bg-studio-danger/10 px-3 py-2.5 text-xs text-studio-danger">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {loadError && (
        <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-studio-danger/25 bg-studio-danger/10 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-studio-danger" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Generations could not be loaded</p>
            <p className="mt-1 text-xs text-studio-muted">Check your connection and try again. Your generations are still saved.</p>
          </div>
          <Button type="button" variant="secondary" loading={isValidating} onClick={() => void mutate().catch(() => undefined)}>
            Try again
          </Button>
        </div>
      )}

      {isLoading && !loadError && jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-studio-border py-20 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-studio-muted" />
          <p className="mt-3 text-sm text-studio-muted">Loading generations…</p>
        </div>
      ) : jobs.length === 0 ? (!loadError && (
        <EmptyState
          icon={isTrash ? Trash2 : filter === "favorites" ? Heart : LayoutGrid}
          title={isTrash ? "Trash is empty" : filter === "favorites" ? "No favorites yet" : "No generations yet"}
          hint={
            isTrash
              ? "Deleted generations stay here until you remove them permanently"
              : filter === "favorites"
                ? "Tap the heart on a generation to keep it here"
                : filter === "completed"
                  ? "No completed generations in this scope"
                  : "Generations from this scope will appear here"
          }
        />
      )) : (
        <>
          <ResultGrid
            jobs={jobs}
            trashMode={isTrash}
            busyJobId={busyJobId}
            selectedIds={isTrash ? undefined : selection.ids}
            onToggleSelect={isTrash ? undefined : selection.toggle}
            onFavoriteChange={() => void mutate()}
            onTrash={(job) => setPendingAction({ type: "trash", jobs: [job] })}
            onRestore={restore}
            onPermanentDelete={(job) => setPendingAction({ type: "purge", jobs: [job] })}
            onMove={(job) => openMove([job])}
            hasMore={hasMore}
            onLoadMore={() => (loadError ? mutate() : setSize(size + 1))}
            columns={grid.columns}
            cardSize={grid.cardSize}
            selectMode={selectMode}
          />
          <div className="mt-6 flex justify-center">
            {hasMore ? (
              <Button
                type="button"
                variant="secondary"
                loading={loadingMore}
                onClick={() => void (loadError ? mutate() : setSize(size + 1)).catch(() => undefined)}
              >
                {loadError ? "Retry loading more" : "Load more"}
              </Button>
            ) : (
              jobs.length > GALLERY_PAGE_SIZE && (
                <p className="text-xs text-studio-muted">All {jobs.length} generations loaded</p>
              )
            )}
          </div>
          {selectMode && (
            <SelectionToolbar
              count={selection.ids.size}
              selectableCount={selection.selectableCount}
              moreAvailable={hasMore}
              selectingAll={selectingAll}
              onSelectAll={() => (hasMore ? setSelectingAll(true) : selection.selectAll())}
              onClear={exitSelection}
              onDeselectAll={() => {
                selection.clear();
                bulkDownload.reset();
              }}
              onDownload={() => void bulkDownload.start(downloadPlan(jobs, selection.ids))}
              download={bulkDownload.state}
              onMove={() => openMove(selection.selected)}
              onTrash={trashSelected}
              busy={busyJobId !== null || projectMove.busy}
              className={FLOATING_BAR}
            />
          )}
          {!isTrash && !selectMode && projectMove.outcome && (
            <MoveResultBar
              outcome={projectMove.outcome}
              busy={projectMove.busy}
              onUndo={() => void projectMove.undo()}
              onDismiss={projectMove.dismiss}
              className={FLOATING_BAR}
            />
          )}
        </>
      )}

      <Dialog
        open={pendingAction !== null}
        onClose={closeDialog}
        title={isPurging ? "Delete permanently?" : pendingCount > 1 ? `Move ${pendingCount} to Trash?` : "Move to Trash?"}
        className="max-w-md"
      >
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                isPurging
                  ? "bg-studio-danger/15 text-studio-danger"
                  : "bg-studio-warning/15 text-studio-warning"
              }`}
            >
              {isPurging ? <AlertTriangle className="h-5 w-5" /> : <Trash2 className="h-5 w-5" />}
            </div>
            <div>
              <p className="text-sm leading-6 text-studio-ink">
                {isPurging
                  ? "This generation and any output files not used by Library or Elements will be removed permanently."
                  : pendingCount > 1
                    ? `These ${pendingCount} generations will move out of Gallery and can be restored later from Trash.`
                    : "This generation will move out of Gallery and can be restored later from Trash."}
              </p>
              {typeof pendingPrompt === "string" && pendingPrompt.trim() && (
                <p className="mt-2 line-clamp-2 rounded-lg bg-studio-card px-3 py-2 text-xs text-studio-muted">
                  {pendingPrompt}
                </p>
              )}
              {pendingAction?.skipped ? (
                <p className="mt-2 text-xs text-studio-muted">
                  {pendingAction.skipped} selected {pendingAction.skipped === 1 ? "generation belongs" : "generations belong"} to other members and will stay in Gallery.
                </p>
              ) : null}
              {isPurging && (
                <p className="mt-2 text-xs font-medium text-studio-danger">This action cannot be undone.</p>
              )}
            </div>
          </div>
          {actionError && (
            <p role="alert" className="mt-4 rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">
              {actionError}
            </p>
          )}
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={closeDialog}
              disabled={busyJobId !== null}
              className="active:scale-[0.96] active:translate-y-0"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={busyJobId !== null}
              onClick={confirmAction}
              className="active:scale-[0.96] active:translate-y-0"
            >
              {isPurging ? "Delete forever" : pendingCount > 1 ? `Move ${pendingCount} to Trash` : "Move to Trash"}
            </Button>
          </div>
        </div>
      </Dialog>

      <MoveToProjectDialog
        open={projectMove.request !== null}
        count={projectMove.request?.ids.length ?? 0}
        noun="generation"
        currentProjectId={projectMove.request?.currentProjectId}
        noProjectHint="Shown only under All projects"
        busy={projectMove.busy}
        error={projectMove.error}
        onClose={projectMove.close}
        onConfirm={(projectId, name) => void projectMove.confirm(projectId, name)}
      />
    </div>
  );
}
